import { useState, useEffect, useCallback } from "react";
import {
  createMerchandiseCategory,
  getMerchandiseCategories,
  editMerchandiseCategory,
  deleteMerchandiseCategory,
} from "../../API/MerchandiseCategoryAPI/MerchandiseCategoryAPI";
import SimplePagination from "../../components/Pagination/SimplePagination";
import Loader from "../../components/Loader/Loader";
import ModelWrapper from "../../components/Modal/ModelWrapper";
import Button from "../../components/button/Button";
import MerchandiseCategoryForm from "../../components/Form/MerchandiseCategoryForm";
import Wrapper from "../../components/wrapper/Wrapper";
import DeleteButton from "../../components/button/DeleteButton";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { FiPlus, FiEdit3, FiTrash2, FiImage, FiTag } from "react-icons/fi";

const MerchandiseCategory = () => {
  const dispatch = useDispatch();
  const { categories, paginationInfo } = useSelector(
    (state) => state.merchandiseCategories
  );
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    file: null,
  });
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const fetchCategories = useCallback(
    async ({ page, limit }) => {
      setLoading(true);
      try {
        await dispatch(
          getMerchandiseCategories({
            page,
            limit,
          })
        );
        setError(null);
      } catch (err) {
        setError("Failed to fetch categories");
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchCategories({ page: pageNumber, limit: pageLimit });
  }, [pageNumber, pageLimit, fetchCategories]);

  const handleEdit = (category) => {
    setFormData({
      id: category._id,
      name: category.name,
      file: category.image,
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    setCategoryToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;
    setDeleteLoading(categoryToDelete);
    setError(null);
    try {
      await dispatch(deleteMerchandiseCategory({ id: categoryToDelete }));
      setIsDeleteModalOpen(false);
      setCategoryToDelete(null);
    } catch (err) {
      setError("Failed to delete category");
      toast.error(err.response.data.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLimitChange = async (newLimit) => {
    setPageLimit(newLimit);
    setPageNumber(1);
  };

  return (
    <Wrapper>
      {/* Form Modal */}
      <ModelWrapper
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        Title={isEditing ? "Edit Category" : "Create Category"}
        isLoading={loading}
        setIsLoading={setLoading}
      >
        <MerchandiseCategoryForm
          pageLimit={pageLimit}
          pageNumber={pageNumber}
          setFormData={setFormData}
          formData={formData}
          setLoading={setLoading}
          loading={loading}
          setError={setError}
          isEditing={isEditing}
          editMerchandiseCategory={editMerchandiseCategory}
          createMerchandiseCategory={createMerchandiseCategory}
          setIsModalOpen={setIsModalOpen}
          fetchCategories={fetchCategories}
          setIsEditing={setIsEditing}
          error={error}
        />
      </ModelWrapper>

      {/* Delete Confirmation Modal */}
      <ModelWrapper
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        Title="Confirm Deletion"
        isLoading={deleteLoading}
        setIsLoading={setDeleteLoading}
      >
        <div className="p-4">
          <p className="text-gray-700 mb-4">
            Are you sure you want to delete this category? This action cannot be
            undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setIsDeleteModalOpen(false)}
              text="Cancel"
              className="bg-gray-300 hover:bg-gray-400"
            />
            <DeleteButton
              onClick={confirmDelete}
              isLoading={deleteLoading}
              text="Delete"
            />
          </div>
        </div>
      </ModelWrapper>

      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Merchandise Categories</h1>
            <p className="text-gray-600 mt-1">Organize and manage your product categories</p>
          </div>
          <button
            onClick={() => {
              setFormData({ id: "", name: "", file: null });
              setIsEditing(false);
              setIsModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors font-medium"
          >
            <FiPlus className="w-4 h-4" />
            Add Category
          </button>
        </div>
      </div>
      {/* Categories Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {categories?.length > 0 && !loading ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Image
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories?.map((category) => (
                  <tr key={category._id} className="hover:bg-gray-50 transition-colors">
                    {/* Category Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-[--primary] rounded-lg flex items-center justify-center">
                            <FiTag className="w-5 h-5 text-white" />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-medium text-gray-900">
                            {category.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Product Category
                          </p>
                        </div>
                      </div>
                    </td>
                    
                    {/* Image Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center">
                        {category.image ? (
                          <img
                            src={category.image}
                            alt={category.name}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                            <FiImage className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </td>
                    
                    {/* Actions Column */}
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(category)}
                          className="p-2 text-gray-400 hover:text-[--primary] hover:bg-[--primary]/10 rounded-lg transition-colors"
                          title="Edit Category"
                        >
                          <FiEdit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(category._id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Category"
                          disabled={deleteLoading === category._id}
                        >
                          {deleteLoading === category._id ? (
                            <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <FiTrash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <></>
        )}
        
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        )}
        
        {/* Empty State */}
        {!loading && categories?.length === 0 && (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FiTag className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
            <p className="text-gray-500 mb-6">Get started by creating your first category.</p>
            <button
              onClick={() => {
                setFormData({ id: "", name: "", file: null });
                setIsEditing(false);
                setIsModalOpen(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors font-medium"
            >
              <FiPlus className="w-4 h-4" />
              Create Category
            </button>
          </div>
        )}
      </div>
      <SimplePagination
        totalPages={paginationInfo?.totalPages || 0}
        currentPage={paginationInfo?.currentPage || 1}
        onPageChange={setPageNumber}
        limit={pageLimit || 10}
        onLimitChange={handleLimitChange}
        totalItems={paginationInfo?.totalItems || 0}
      />
    </Wrapper>
  );
};

export default MerchandiseCategory;
